import { useMemo, useState } from 'react';

import type { TableSelectorOption, UseTableSelectionResult } from '../types';

/**
 * Selection state behind a TableSelector whose `options` are one entry per
 * table (matched by `tableId`) plus, optionally, an entry that is not a table
 * (the "Analytical dataset" option).
 */
export const useTableSelection = <T extends { tableId: string }>(
  tables: T[],
  options: TableSelectorOption[]
): UseTableSelectionResult<T> => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Defaults to the first option once options load, and re-anchors there if the
  // selection no longer matches an option (e.g. a different version loads with
  // fewer tables).
  const resolvedSelectedId = useMemo(() => {
    if (options.length === 0) return null;
    if (selectedId && options.some((option) => option.id === selectedId)) return selectedId;
    return options[0].id;
  }, [options, selectedId]);

  const selectedTableIndex = useMemo(
    () =>
      resolvedSelectedId ? tables.findIndex((table) => table.tableId === resolvedSelectedId) : -1,
    [tables, resolvedSelectedId]
  );

  const selectedTable = selectedTableIndex === -1 ? null : tables[selectedTableIndex];

  return {
    selectedId: resolvedSelectedId,
    onSelect: setSelectedId,
    selectedTable,
    selectedTableNumber: selectedTableIndex + 1,
    isAnalyticalDatasetSelected: Boolean(resolvedSelectedId) && !selectedTable,
  };
};
